import copy from '../../copy/en/auth.json' with { type: 'json' };
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { ArrowLeft, KeyRound, Send } from "lucide-react";
import { Link, useLocation } from "react-router";
import { useSession } from "./SessionProvider.js";
import { createRecoveryClient, passwordRecoveryCoordinator, requestPasswordRecovery, type PasswordRecoveryCoordinator, type RecoveryResult } from "./passwordRecovery.js";
import type { ActivationAuthClient } from "./activation.js";
import type { PortalConfig } from "../lib/config.js";
import { Button } from "../components/ui/Button.js";
import { Callout } from "../components/ui/Callout.js";
import { Field, Input } from "../components/ui/Form.js";
import { FullPageSpinner } from "../components/ui/FullPage.js";
import { Brand } from "../shell/Brand.js";
import { ModeToggle } from "../shell/TopBar.js";

function RecoveryFrame({ title, children }: { title: string; children: ReactNode }) {
  return <div className="fullpage"><div className="fullpage__mode"><ModeToggle /></div><div className="fullpage__card">
    <div className="fullpage__hero"><Brand size="lg" to={null} /><h1 className="fullpage__title">{title}</h1></div>
    <div className="stack">{children}<Link className="btn btn--ghost" to="/join?mode=sign-in"><ArrowLeft className="btn__icon" aria-hidden />{copy.PasswordRecoveryPage.backToSignIn}</Link></div>
  </div></div>;
}

export function ForgotPasswordPage() {
  const { supabase, authMode } = useSession();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const submitting = useRef(false);
  const emailInput = useRef<HTMLInputElement>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (submitting.current || sent || !supabase || authMode !== "jwt") return;
    if (!email.trim() || !emailInput.current?.validity.valid) { setEmailError(copy.PasswordRecoveryPage.enterAValidEmailAddress); emailInput.current?.focus(); return; }
    setEmailError(null);
    submitting.current = true;
    setBusy(true);
    setError(null);
    try {
      const result = await requestPasswordRecovery(supabase, email, window.location.origin);
      if (result === "acknowledged") setSent(true);
      else setError(result === "wait" ? copy.PasswordRecoveryPage.pleaseWaitAMinuteBeforeRequestingAnother : copy.PasswordRecoveryPage.weCouldNotRequestAResetLink);
    } finally { submitting.current = false; setBusy(false); }
  }

  return <RecoveryFrame title={copy.PasswordRecoveryPage.forgotPassword}>
    {sent ? <Callout tone="success">{copy.PasswordRecoveryPage.ifAnAccountExistsForThatEmail}</Callout>
      : authMode !== "jwt" || !supabase ? <Callout tone="warning">{copy.PasswordRecoveryPage.passwordRecoveryIsUnavailableInThisEnvironment}</Callout>
        : <form className="login-form" noValidate onSubmit={event => void submit(event)}>
          <p>{copy.PasswordRecoveryPage.enterTheEmailAddressYouUseTo}</p>
          <Field label={copy.PasswordRecoveryPage.email} error={emailError}>{control => <Input {...control} ref={emailInput} type="email" autoComplete="username" value={email} onChange={event => { setEmail(event.target.value); setEmailError(null); }} disabled={busy} />}</Field>
          {error ? <Callout tone="danger">{error}</Callout> : null}
          <Button type="submit" variant="primary" icon={Send} loading={busy}>{copy.PasswordRecoveryPage.sendResetLink}</Button>
        </form>}
  </RecoveryFrame>;
}

interface ResetPasswordPageProps {
  coordinator?: PasswordRecoveryCoordinator;
  createClient?: (config: PortalConfig) => ActivationAuthClient | null;
}

export function ResetPasswordPage(props: ResetPasswordPageProps = {}) {
  const location = useLocation();
  return <ResetPasswordAttempt key={`${location.key}:${location.search}:${location.hash}`} {...props} />;
}

function ResetPasswordAttempt({ coordinator = passwordRecoveryCoordinator, createClient = createRecoveryClient }: ResetPasswordPageProps) {
  const { config } = useSession();
  const [state, setState] = useState<RecoveryResult | { kind: "confirmation" } | { kind: "loading" } | { kind: "complete" }>({ kind: "confirmation" });
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [fieldErrors, setFieldErrors] = useState<{ password?: string; confirmation?: string }>({});
  const passwordInput = useRef<HTMLInputElement>(null);
  const confirmationInput = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submitting = useRef(false);
  const clientRef = useRef<ActivationAuthClient | null>(null);
  const active = useRef(true);
  const continuing = useRef(false);

  coordinator.capture(new URL(window.location.href), () => window.history.replaceState(window.history.state, document.title, window.location.pathname));
  useEffect(() => {
    active.current = true;
    const release = coordinator.retain();
    const started = coordinator.started();
    if (started) {
      clientRef.current = coordinator.acquireClient(() => createClient(config));
      setState({ kind: "loading" });
      void started.then(result => { if (active.current) setState(result); });
    } else setState(coordinator.landingState());
    return () => { active.current = false; release(); };
  }, [config, coordinator, createClient]);

  async function continueSetup() {
    if (continuing.current || state.kind !== "confirmation") return;
    continuing.current = true;
    setState({ kind: "loading" });
    const client = coordinator.acquireClient(() => createClient(config));
    clientRef.current = client;
    if (!client) { setState({ kind: "invalid", reason: "unavailable" }); return; }
    const result = await coordinator.initialize(client);
    if (active.current) setState(result);
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    const client = clientRef.current;
    if (submitting.current || state.kind !== "ready" || !client) return;
    setError(null);
    setFieldErrors({});
    if (password.length < 8) { setFieldErrors({ password: copy.PasswordRecoveryPage.chooseAPasswordWithAtLeast8 }); passwordInput.current?.focus(); return; }
    if (password !== confirmation) { setFieldErrors({ confirmation: copy.PasswordRecoveryPage.passwordsDoNotMatch }); confirmationInput.current?.focus(); return; }
    submitting.current = true;
    setBusy(true);
    try {
      // Recheck the isolated identity immediately before changing credentials.
      const checked = await client.auth.getUser();
      const user = checked.data.user;
      if (!active.current) return;
      if (checked.error || !user || user.id !== state.identity.id || user.email?.toLowerCase() !== state.identity.email.toLowerCase() || !user.email_confirmed_at || user.is_anonymous) {
        coordinator.clear(); setState({ kind: "invalid", reason: "expired" }); return;
      }
      const updated = await client.auth.updateUser({ password });
      if (!active.current) return;
      if (updated.error || updated.data.user?.id !== state.identity.id) {
        setError(copy.PasswordRecoveryPage.yourPasswordCouldNotBeChangedTry); return;
      }
      setPassword(""); setConfirmation("");
      coordinator.clear();
      setState({ kind: "complete" });
      // Never sign out or replace a different person's existing main session.
    } catch {
      if (active.current) setError(copy.PasswordRecoveryPage.yourPasswordCouldNotBeChangedCheck);
    } finally { submitting.current = false; if (active.current) setBusy(false); }
  }

  if (state.kind === "loading") return <FullPageSpinner label={copy.PasswordRecoveryPage.checkingYourPasswordResetLink} />;
  return <RecoveryFrame title={copy.PasswordRecoveryPage.resetYourPassword}>
    {state.kind === "complete" ? <Callout tone="success">{copy.PasswordRecoveryPage.yourPasswordHasBeenChangedSignIn}</Callout>
      : state.kind === "invalid" ? <Callout tone="danger" title={copy.PasswordRecoveryPage.resetLinkUnavailable}>{state.reason === "expired" ? copy.PasswordRecoveryPage.thisResetLinkHasExpiredOrHas : copy.PasswordRecoveryPage.thisResetLinkIsMissingInvalidOr} <Link to="/forgot-password">{copy.PasswordRecoveryPage.requestANewResetLink}</Link>.</Callout>
        : state.kind === "confirmation" ? <><p>{copy.PasswordRecoveryPage.continueToVerifyYourResetLinkAnd}</p><Button type="button" variant="primary" onClick={() => void continueSetup()}>{copy.PasswordRecoveryPage.continueToPasswordSetup}</Button></>
        : <form className="login-form" noValidate onSubmit={event => void submit(event)}>
          <Field label={copy.PasswordRecoveryPage.verifiedEmail}>{control => <Input {...control} type="email" autoComplete="username" readOnly value={state.identity.email} />}</Field>
          <Field label={copy.PasswordRecoveryPage.newPassword} error={fieldErrors.password} help={copy.PasswordRecoveryPage.useAtLeast8Characters}>{control => <Input {...control} ref={passwordInput} type="password" autoComplete="new-password" value={password} onChange={event => { setPassword(event.target.value); setFieldErrors({}); }} disabled={busy} />}</Field>
          <Field label={copy.PasswordRecoveryPage.confirmPassword} error={fieldErrors.confirmation}>{control => <Input {...control} ref={confirmationInput} type="password" autoComplete="new-password" value={confirmation} onChange={event => { setConfirmation(event.target.value); setFieldErrors(current => ({ ...current, confirmation: undefined })); }} disabled={busy} />}</Field>
          {error ? <Callout tone="danger">{error}</Callout> : null}
          <Button type="submit" variant="primary" icon={KeyRound} loading={busy}>{copy.PasswordRecoveryPage.setNewPassword}</Button>
        </form>}
  </RecoveryFrame>;
}
